<template>
  <div class="row delivery-hero-integration-form">
    <div class="col-md-12">
      <div class="delivery-hero-integration-form__setup" role="status">
        <p class="delivery-hero-integration-form__setup-title">{{ $t('gestiune.integrationsDeliveryHeroSetupTitle') }}</p>
        <ol class="delivery-hero-integration-form__setup-steps">
          <li>{{ $t('gestiune.integrationsDeliveryHeroSetupStep1') }}</li>
          <li>{{ $t('gestiune.integrationsDeliveryHeroSetupStep2') }}</li>
          <li>{{ $t('gestiune.integrationsDeliveryHeroSetupStep3') }}</li>
          <li>{{ $t('gestiune.integrationsDeliveryHeroSetupStep4') }}</li>
        </ol>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('location_id') }">
        <label>{{ fieldLabel('location_id') }}<span v-if="isRequired('location_id')" class="text-danger"> *</span></label>
        <select
          v-model="formData.location_id"
          class="form-control"
          :disabled="disabled"
        >
          <option :value="null">{{ $t('general.select') }}</option>
          <option
            v-for="loc in locations"
            :key="'delivery-hero-loc-' + String(loc._integrationLocationId)"
            :value="loc._integrationLocationId"
            :disabled="isLocationOccupied(loc._integrationLocationId)"
            :class="{ 'integration-location-option--occupied': isLocationOccupied(loc._integrationLocationId) }"
          >
            {{ locationOptionLabel(loc) }}
          </option>
        </select>
        <span v-if="showFieldError('location_id')" class="text-danger help-block">{{ fieldErrorMessage('location_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('api_url') }">
        <label>{{ fieldLabel('api_url') }}<span v-if="isRequired('api_url')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.api_url"
          :disabled="disabled"
        >
        <span v-if="showFieldError('api_url')" class="text-danger help-block">{{ fieldErrorMessage('api_url') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group delivery-hero-integration-form__switch-row">
        <label class="delivery-hero-integration-form__switch-label">&nbsp;</label>
        <div class="delivery-hero-integration-form__switch-control">
          <p-switch
            v-model="formData.active"
            type="success"
            class="mar-bt-0"
            name="delivery-hero-active"
            :disabled="disabled"
          />
          <label for="delivery-hero-active">{{ fieldLabel('active') }}</label>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="delivery-hero-integration-form__keys">
        <p class="delivery-hero-integration-form__keys-title">{{ $t('gestiune.integrationsDeliveryHeroKeysTitle') }}</p>
        <p v-if="!formData.public_key" class="help-block text-muted">
          {{ $t('gestiune.integrationsDeliveryHeroKeysPending') }}
        </p>
        <div v-else class="form-group">
          <label>{{ fieldLabel('public_key') }}</label>
          <textarea
            class="form-control"
            rows="6"
            :value="formData.public_key"
            readonly
          />
          <div class="delivery-hero-integration-form__key-actions">
            <button
              type="button"
              class="btn btn-info btn-sm delivery-hero-integration-form__key-btn"
              :disabled="disabled"
              @click.prevent="copyPublicKey"
            >
              {{ $t('gestiune.integrationsDeliveryHeroCopyPublicKey') }}
            </button>
            <button
              type="button"
              class="btn btn-info btn-sm delivery-hero-integration-form__key-btn"
              :disabled="disabled"
              @click.prevent="downloadPublicKey"
            >
              {{ $t('gestiune.integrationsDeliveryHeroDownloadPublicKey') }}
            </button>
            <button
              v-if="canRegenerateKeys"
              type="button"
              class="btn btn-warning btn-sm delivery-hero-integration-form__key-btn"
              :disabled="disabled || regeneratingKeys"
              @click.prevent="$emit('regenerate-keys')"
            >
              {{ $t('gestiune.integrationsDeliveryHeroRegenerateKeys') }}
            </button>
          </div>
          <small class="help-block text-muted">{{ $t('gestiune.integrationsDeliveryHeroPublicKeyHint') }}</small>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <p class="delivery-hero-integration-form__section-title">{{ $t('gestiune.integrationsDeliveryHeroCredentialsTitle') }}</p>
      <p class="help-block text-muted mar-bt-10">{{ $t('gestiune.integrationsDeliveryHeroCredentialsHint') }}</p>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('client_id') }">
        <label>{{ fieldLabel('client_id') }}<span v-if="isRequired('client_id')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.client_id"
          :disabled="disabled"
        >
        <span v-if="showFieldError('client_id')" class="text-danger help-block">{{ fieldErrorMessage('client_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('key_id') }">
        <label>{{ fieldLabel('key_id') }}<span v-if="isRequired('key_id')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.key_id"
          :disabled="disabled"
        >
        <span v-if="showFieldError('key_id')" class="text-danger help-block">{{ fieldErrorMessage('key_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('scope') }">
        <label>{{ fieldLabel('scope') }}<span v-if="isRequired('scope')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.scope"
          :disabled="disabled"
          :placeholder="$t('gestiune.integrationsDeliveryHeroScopePlaceholder')"
        >
        <span v-if="showFieldError('scope')" class="text-danger help-block">{{ fieldErrorMessage('scope') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('webhook_secret') }">
        <label>{{ fieldLabel('webhook_secret') }}</label>
        <input
          type="text"
          class="form-control"
          v-model="formData.webhook_secret"
          :disabled="disabled"
        >
        <span v-if="showFieldError('webhook_secret')" class="text-danger help-block">{{ fieldErrorMessage('webhook_secret') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="delivery-hero-integration-form__auto-send-row">
        <div class="delivery-hero-integration-form__auto-send-status">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_at_status') }">
            <label>{{ fieldLabel('auto_send_at_status') }}</label>
            <select
              v-model="formData.auto_send_at_status"
              class="form-control"
              :disabled="disabled"
            >
              <option
                v-for="opt in autoSendStatusOptions"
                :key="'delivery-hero-status-' + opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_at_status')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_at_status') }}</span>
          </div>
        </div>
        <div class="delivery-hero-integration-form__auto-send-time">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_time') }">
            <label>{{ fieldLabel('auto_send_time') }}</label>
            <select
              v-model="formData.auto_send_time"
              class="form-control"
              :disabled="disabled || isAutoSendManual"
            >
              <option
                v-for="mins in autoSendTimeOptions"
                :key="'delivery-hero-time-' + mins"
                :value="mins"
              >
                {{ mins }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_time')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_time') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="form-group" :class="{ 'has-error': showFieldError('feedback_emails') }">
        <label>{{ fieldLabel('feedback_emails') }}</label>
        <textarea
          class="form-control"
          rows="3"
          v-model="formData.feedback_emails"
          :disabled="disabled"
          :placeholder="$t('gestiune.integrationsFeedbackEmailsHint')"
        />
        <small class="help-block text-muted">{{ $t('gestiune.integrationsFeedbackEmailsHint') }}</small>
        <span v-if="showFieldError('feedback_emails')" class="text-danger help-block">{{ fieldErrorMessage('feedback_emails') }}</span>
      </div>
    </div>

    <div v-if="formData.status_callback_url || formData.refund_callback_url" class="col-md-12">
      <div class="delivery-hero-integration-form__callbacks">
        <p class="delivery-hero-integration-form__callbacks-title">{{ $t('gestiune.integrationsDeliveryHeroCallbacksTitle') }}</p>
        <div v-if="formData.status_callback_url" class="form-group">
          <label>{{ $t('gestiune.integrationsDeliveryHeroStatusCallback') }}</label>
          <input type="text" class="form-control" :value="formData.status_callback_url" readonly>
        </div>
        <div v-if="formData.refund_callback_url" class="form-group">
          <label>{{ $t('gestiune.integrationsDeliveryHeroRefundCallback') }}</label>
          <input type="text" class="form-control" :value="formData.refund_callback_url" readonly>
        </div>
        <small class="help-block text-muted">{{ $t('gestiune.integrationsDeliveryHeroCallbacksHint') }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import PSwitch from 'src/components/UIComponents/Switch.vue'
import {
  WOLT_DRIVE_AUTO_SEND_STATUS_VALUES,
  WOLT_DRIVE_AUTO_SEND_TIME_VALUES
} from 'src/components/Dashboard/Views/Integrations/WoltDriveIntegrationForm.vue'
import {
  getIntegrationFieldErrorMessage,
  hasIntegrationFieldError
} from 'src/components/Dashboard/Views/Integrations/integrationFormFieldHelpers.js'

export const DEFAULT_DELIVERY_HERO_API_URL = 'https://ondemand-api-glovoapp.deliveryhero.io/ro'

export const DELIVERY_HERO_AUTO_SEND_STATUS_VALUES = WOLT_DRIVE_AUTO_SEND_STATUS_VALUES
export const DELIVERY_HERO_AUTO_SEND_TIME_VALUES = WOLT_DRIVE_AUTO_SEND_TIME_VALUES

export default {
  name: 'DeliveryHeroIntegrationForm',
  components: {
    PSwitch
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fieldLabelFn: {
      type: Function,
      default: null
    },
    requiredFields: {
      type: Array,
      default: () => []
    },
    credentialFields: {
      type: Array,
      default: () => []
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    showFieldValidation: {
      type: Boolean,
      default: false
    },
    occupiedLocationIds: {
      type: Array,
      default: () => []
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    regeneratingKeys: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    autoSendStatusOptions () {
      return DELIVERY_HERO_AUTO_SEND_STATUS_VALUES.map(value => ({
        value,
        label: this.autoSendStatusLabel(value)
      }))
    },
    autoSendTimeOptions () {
      return DELIVERY_HERO_AUTO_SEND_TIME_VALUES.slice()
    },
    isAutoSendManual () {
      return String(this.formData.auto_send_at_status || '').toUpperCase() === 'MANUAL'
    },
    showCredentialRequired () {
      return !this.isCreateMode || !!this.formData.public_key
    },
    canRegenerateKeys () {
      return !this.isCreateMode && !!this.formData.id && !!this.formData.public_key
    }
  },
  created () {
    this.ensureDefaults()
  },
  methods: {
    fieldLabel (field) {
      if (typeof this.fieldLabelFn === 'function') return this.fieldLabelFn(field)
      return field
    },
    isRequired (field) {
      if ((this.requiredFields || []).includes(field)) return true
      // Credentials are required for Validate (shown after keys exist), not for first Save.
      return this.showCredentialRequired && (this.credentialFields || []).includes(field)
    },
    showFieldError (field) {
      if (this.validationErrors && this.validationErrors[field]) return true
      if (!this.showFieldValidation) return false
      // Only empty-check save-required fields here; credential emptiness is enforced on Validate.
      if (!(this.requiredFields || []).includes(field)) return false
      return hasIntegrationFieldError({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: true,
        requiredFields: this.requiredFields,
        formData: this.formData
      })
    },
    fieldErrorMessage (field) {
      return getIntegrationFieldErrorMessage({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: this.showFieldValidation,
        requiredFields: this.requiredFields,
        formData: this.formData,
        requiredMessage: this.$t('validations.requiredField')
      })
    },
    isLocationOccupied (locationId) {
      if (locationId == null || locationId === '') return false
      return (this.occupiedLocationIds || []).map(String).includes(String(locationId))
    },
    locationOptionLabel (loc) {
      const name = loc && loc.name ? loc.name : ''
      if (this.isLocationOccupied(loc._integrationLocationId)) {
        return `${name} (${this.$t('gestiune.integrationsLocationOccupied')})`
      }
      return name
    },
    autoSendStatusLabel (value) {
      const key = `gestiune.woltDriveAutoSendStatus_${String(value || '').toLowerCase()}`
      const translated = this.$t(key)
      return translated !== key ? translated : String(value || '')
    },
    copyPublicKey () {
      const key = this.formData.public_key
      if (!key) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(key)
        return
      }
      const el = document.createElement('textarea')
      el.value = key
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    },
    downloadPublicKey () {
      const key = this.formData.public_key
      if (!key) return
      const blob = new Blob([key], { type: 'application/x-pem-file' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'delivery-hero-odr.pub'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    ensureDefaults () {
      const url = this.formData.api_url
      if (url === undefined || url === null || String(url).trim() === '') {
        this.$set(this.formData, 'api_url', DEFAULT_DELIVERY_HERO_API_URL)
      }

      if (this.formData.active === undefined || this.formData.active === null) {
        this.$set(this.formData, 'active', true)
      }

      let status = this.formData.auto_send_at_status
      status = status === undefined || status === null || String(status).trim() === ''
        ? ''
        : String(status).trim().toUpperCase()
      if (!status || !DELIVERY_HERO_AUTO_SEND_STATUS_VALUES.includes(status)) {
        status = 'MANUAL'
      }
      this.$set(this.formData, 'auto_send_at_status', status)

      let time = this.formData.auto_send_time
      time = time === null || time === undefined || String(time).trim() === ''
        ? ''
        : String(parseInt(String(time).trim(), 10))
      if (!time || !DELIVERY_HERO_AUTO_SEND_TIME_VALUES.includes(time)) {
        time = '30'
      }
      this.$set(this.formData, 'auto_send_time', time)
    }
  }
}
</script>

<style scoped>
.delivery-hero-integration-form__setup {
  margin-bottom: 18px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #eef0fb;
  border: 1px solid #c5cae9;
  color: #25396f;
}
.delivery-hero-integration-form__setup-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
}
.delivery-hero-integration-form__setup-steps {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.5;
}
.delivery-hero-integration-form__setup-steps li + li {
  margin-top: 6px;
}
.delivery-hero-integration-form__keys {
  margin-bottom: 18px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f7f8fc;
  border: 1px solid #e0e3ef;
}
.delivery-hero-integration-form__keys-title,
.delivery-hero-integration-form__section-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #25396f;
}
.delivery-hero-integration-form__key-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.delivery-hero-integration-form__key-btn {
  text-transform: none !important;
  font-weight: 400;
  border-radius: 50px;
  border-width: 1px;
  border-style: solid;
  box-sizing: border-box;
}
.delivery-hero-integration-form__key-btn.btn-info {
  background: #E4F4FF;
  color: #0097FC;
  border-color: transparent;
}
.delivery-hero-integration-form__key-btn.btn-info:hover,
.delivery-hero-integration-form__key-btn.btn-info:focus {
  background: #E4F4FF;
  color: #0097FC;
  border-color: #0097FC;
}
.delivery-hero-integration-form__key-btn.btn-warning {
  background: #FEF1DC;
  color: #F39E1A;
  border-color: transparent;
}
.delivery-hero-integration-form__key-btn.btn-warning:hover,
.delivery-hero-integration-form__key-btn.btn-warning:focus {
  background: #FEF1DC;
  color: #F39E1A;
  border-color: #F39E1A;
}
.delivery-hero-integration-form__auto-send-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.delivery-hero-integration-form__auto-send-status {
  flex: 2 1 0;
  min-width: 0;
}
.delivery-hero-integration-form__auto-send-time {
  flex: 1 0 150px;
  min-width: 150px;
  max-width: 200px;
}
.delivery-hero-integration-form__auto-send-time label {
  white-space: nowrap;
}
.delivery-hero-integration-form__switch-row {
  margin-bottom: 0;
}
.delivery-hero-integration-form__switch-control {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
}
.delivery-hero-integration-form__callbacks {
  margin-top: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f7f8fc;
  border: 1px solid #e0e3ef;
}
.delivery-hero-integration-form__callbacks-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
}
.delivery-hero-integration-form .has-error .form-control {
  border-color: #dc3545;
}
.delivery-hero-integration-form .help-block {
  margin-top: 4px;
  margin-bottom: 0;
}
.delivery-hero-integration-form option.integration-location-option--occupied {
  color: #9e9e9e;
}
.mar-bt-10 {
  margin-bottom: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/DeliveryHeroIntegrationForm.vue